import { shortDate } from "./format";
import type { Tone } from "./orders";
import type { SupplierInvoice } from "./types";

// What the Invoices screen works out from invoice_status: each supplier invoice's state in words, when it's due, the
// early-pay discount, and which checks failed. Pure, so it is tested without a browser.

const STATUS: Record<string, { text: string; tone: Tone }> = {
  RECEIVED: { text: "Received, not matched yet", tone: "neutral" },
  EXCEPTION: { text: "Didn't match", tone: "late" },
  MATCHED: { text: "Matched", tone: "good" },
  BILLED: { text: "Billed in QuickBooks", tone: "good" },
  SCHEDULED: { text: "Payment scheduled", tone: "good" },
  PAID: { text: "Paid", tone: "good" },
  REJECTED: { text: "Rejected", tone: "neutral" },
};

export function invoiceStatus(status: string): { text: string; tone: Tone } {
  return STATUS[status] ?? { text: status.toLowerCase().replace(/_/g, " "), tone: "neutral" };
}

function days(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
}

/** When it's due, in words, with a tone: past due, due within a week, or later. Null when there's no due date. */
export function dueWords(inv: SupplierInvoice, today: string): { text: string; tone: Tone } | null {
  if (inv.paidOn) return { text: `Paid ${shortDate(inv.paidOn)}`, tone: "good" };
  if (!inv.dueDate) return null;
  const left = days(today, inv.dueDate);
  if (left < 0) return { text: `${-left} ${left === -1 ? "day" : "days"} past due`, tone: "late" };
  if (left === 0) return { text: "Due today", tone: "soon" };
  return { text: `Due ${shortDate(inv.dueDate)}`, tone: left <= 7 ? "soon" : "neutral" };
}

/** The early-pay discount still open, in words ("Take $12.54 by Fri 6 Nov"), or null when there's none or it's gone. */
export function discountWords(inv: SupplierInvoice, today: string): { text: string; tone: Tone } | null {
  if (inv.paidOn || !inv.discountDeadline || !inv.discountAmount) return null;
  const left = days(today, inv.discountDeadline);
  if (left < 0) return null;
  const amount = inv.discountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  return { text: `${amount} off if paid by ${shortDate(inv.discountDeadline)}`, tone: left <= 3 ? "soon" : "neutral" };
}

const CHECK: Record<string, string> = {
  INV_COST: "Price", INV_QTY: "Quantity", INV_OVER_SHIPPED: "More than shipped", INV_TOTAL: "Total", INV_FREIGHT: "Freight",
  INV_TERMS: "Terms", INV_NO_PO: "No PO", INV_LINE_UNKNOWN: "Unknown line", INV_MATCH: "Match", INV_PARSE_FAILED: "Couldn't read",
};

/** The checks an invoice failed, each with a short name and the platform's message. */
export function failedChecks(inv: SupplierInvoice): { check: string; message: string }[] {
  return (inv.matchResult?.exceptions ?? []).map((e) => ({
    check: CHECK[e.kind] ?? e.kind.replace(/^INV_/, "").toLowerCase().replace(/_/g, " "),
    message: e.message,
  }));
}

/** Didn't match first, then soonest due (no due date last), then newest. */
export function invoiceOrder(rows: SupplierInvoice[]): SupplierInvoice[] {
  return [...rows].sort((a, b) => Number(b.status === "EXCEPTION") - Number(a.status === "EXCEPTION") ||
    (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || (b.invoiceDate ?? "").localeCompare(a.invoiceDate ?? ""));
}
